import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { actions as sharedActions } from "@shared/store";
import { PaymentFailedMessage } from "@shared/constants";

type Options = {
  delay?: number;
  maxAttempts?: number;
};

export default function usePolling(action: () => void, options: Options = {}) {
  const [timer, setTimer] = useState<NodeJS.Timer | null>(null);
  const dispatch = useDispatch();
  const { delay = 3000, maxAttempts = 100 } = options;
  const attemptsRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  }, [timer]);

  const startPolling = useCallback(() => {
    if (timer) {
      stopPolling();
    }
    setTimer(
      setInterval(() => {
        if (attemptsRef.current++ > maxAttempts) {
          stopPolling();
          dispatch(
            sharedActions.showNotification({
              message: PaymentFailedMessage,
              appearance: "error",
            }),
          );

          return;
        }

        action();
      }, delay),
    );
  }, [action, delay, stopPolling, timer, attemptsRef, maxAttempts, dispatch]);

  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling],
  );

  return { startPolling, stopPolling, isPolling: !!timer };
}
